import type { DatePickerProps } from 'antd';
import { DatePicker, Input, Modal, Table, Row, Col, Tag, Spin } from 'antd';
import styles from './index.module.scss';
import './index.scss';
import { PreviewJournalProps } from './types';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { getApi, postApi } from 'redux/apis';
import { base64ToBlob, checkPermission, toastText } from 'utils/utils';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { previewJournalAction } from 'redux/action/journalAction';
import { NumericFormat } from 'react-number-format';
import { CloseSvg } from 'utils/svgs';
import { EJournalStatus } from 'constants/Data';
import { useNavigate } from 'react-router-dom';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DownCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { DuplicateJournalErrorModal } from 'components/Global/DuplicateJournalErrorModal';
import { ResolveClassReferencesModal } from 'components/CostAllocation/ResolveClassReferencesModal';

dayjs.extend(utc);
dayjs.extend(timezone);

const useLoadingMessages = () => {
	const [messageIndex, setMessageIndex] = useState(0);

	const loadingMessages = [
		'Fetching GL accounts...',
		'Loading employee costs...',
		'Processing cost allocations...',
		'Calculating totals...',
		'Creating Journal Entries...',
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
		}, 3000);

		return () => clearInterval(interval);
	}, []);

	return loadingMessages[messageIndex];
};

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const { TextArea } = Input;

const PreviewJournal = (props: PreviewJournalProps) => {
	const navigate = useNavigate();

	const {
		handleClosePreviewJournal,
		isPreviewJournalOpen,
		payPeriodLastDate,
		payPeriodId,
		payPeriodData,
		getJournalListingData,
	} = props;

	const { Column } = Table;

	// const startDate = moment(payPeriodData?.startDate).format('MM/DD/YYYY');
	// const endDate = moment(payPeriodData?.endDate).format('MM/DD/YYYY');

	const startDate = moment(payPeriodData?.startDate)
		.tz(TIMEZONE)
		.format('MM/DD/YYYY');
	const endDate = moment(payPeriodData?.endDate)
		.tz(TIMEZONE)
		.format('MM/DD/YYYY');

	const [isAccountClosed, setIsAccountClosed] = useState<boolean>(false);

	const [, setAutoJournalNo] = useState(1);

	const dispatch = useDispatch<AppDispatch>();

	const { journalData: journalEntries, isLoading } = useSelector(
		(state: any) => state?.journal
	);

	// const [journalData, setJournalData] = useState<any>({
	// 	journalDate: dayjs(payPeriodLastDate),
	// 	journalNumber: 1,
	// 	notes: '',
	// 	qboJournalNumber: null,
	// });
	const currentLoadingMessage = useLoadingMessages();

	const [journalData, setJournalData] = useState<any>({
		journalDate: dayjs(payPeriodLastDate).tz(TIMEZONE, true),
		journalNumber: 1,
		notes: '',
		qboJournalNumber: null,
	});

	const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
	const modalContentRef = useRef<HTMLDivElement>(null);

	const isExcludeEmployeeEnabled = useSelector(
		(state: any) => state.companies?.currentCompany?.isEmployeeExcludedInJournal
	);

	const [attachmentData, setAttachmentData] = useState<string>('');
	const [attachmentFileName, setAttachmentFileName] = useState<string>('');
	const [publishIsLoading, setPublishIsLoading] = useState<boolean>(false);
	const [draftIsLoading, setDraftIsLoading] = useState<boolean>(false);
	const [journalExists, setJournalExists] = useState<boolean>(false);
	const [showDuplicateErrorModal, setShowDuplicateErrorModal] = useState<boolean>(false);
	const [duplicateErrorData, setDuplicateErrorData] = useState<any>(null);
	// CAP-94: publish is blocked server-side when a line's class reference is
	// stale (merged/deactivated in QBO) - catch that specific error and offer
	// the fix inline instead of leaving the admin with just a toast.
	const [showResolveClassModal, setShowResolveClassModal] = useState<boolean>(false);

	const { closingDate } = useSelector((state: any) => state?.closingDate);

	const { configurations: configurationsData } = useSelector(
		(state: any) => state.companies
	);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	const isJournalEntriesPermissionEdit = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Journals Entries',
			permission: ['edit'],
		}
	);

	const isJournalEntriesPermissionAdd = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Journals Entries',
			permission: ['add'],
		}
	);

	useEffect(() => {
		if (payPeriodId) {
			fetchJournalDetails();
			fetchJournalData();
			createCostAllocationCsv();
		}
	}, []);

	const handleScroll = () => {
		if (modalContentRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
			const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

			// Show button when scrolled more than 200px from top AND not at bottom
			setShowScrollToBottom(scrollTop > 200 && !isNearBottom);
		}
	};

	const scrollToBottom = () => {
		if (modalContentRef.current) {
			modalContentRef.current.scrollTo({
				top: modalContentRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	};

	// Add this useEffect after your existing useEffect hooks
	useEffect(() => {
		const modalContent = modalContentRef.current;
		if (modalContent) {
			modalContent.addEventListener('scroll', handleScroll);
			return () => {
				modalContent.removeEventListener('scroll', handleScroll);
			};
		}
	}, [isPreviewJournalOpen]);

	useEffect(() => {
		//

		if (journalEntries && journalEntries.length) {
			let notes = ``;
			notes += `${startDate} - ${endDate}`;

			if (!isExcludeEmployeeEnabled) {
				const employeeNames: any[] = [''];

				journalEntries.forEach((e: any) => {
					if (!employeeNames.includes(e.employeeName)) {
						employeeNames.push(e.employeeName);
					}
				});
				employeeNames.shift();
				notes += ` ${employeeNames.join(', ')}`;
			}

			// if (journalData && !journalData.notes) {
			setJournalData((prevData: any) => {
				return {
					...prevData,
					notes: notes,
				};
			});
			// }
		}
	}, [journalEntries]);

	// useEffect(() => {
	// 	if (payPeriodData && closingDate) {
	// 		if (dayjs(payPeriodData.endDate) < dayjs(closingDate).endOf('day')) {
	// 			setIsAccountClosed(true);
	// 		}
	// 	}
	// }, [payPeriodData, closingDate]);

	useEffect(() => {
		if (payPeriodData && closingDate) {
			const endDate = dayjs(payPeriodData.endDate).tz(TIMEZONE);
			const closing = dayjs(closingDate).tz(TIMEZONE).endOf('day');

			if (endDate.isBefore(closing)) {
				setIsAccountClosed(true);
			}
		}
	}, [payPeriodData, closingDate]);

	const dateChangeHandler: DatePickerProps['onChange'] = (date: any) => {
		setJournalData((prevData: any) => {
			return {
				...prevData,
				journalDate: date,
			};
		});
	};

	// const journalNoChangeHandler = (number: any) => {
	// 	setJournalData((prevData: any) => {
	// 		return {
	// 			...prevData,
	// 			journalNumber: number,
	// 		};
	// 	});
	// };

	const notesHandler = (value: any) => {
		setJournalData((prevData: any) => {
			return {
				...prevData,
				notes: value.target.value,
			};
		});
	};

	const handleSubmit = async (status: number) => {
		try {
			//Check for Notes length limit
			if (journalData?.notes?.length > 4000) {
				toastText('The Notes field must not exceed 4000 characters', 'error');
				return;
			}
			if (journalEntries && journalEntries.length > 0) {
				if (status === EJournalStatus.PUBLISHED) {
					setPublishIsLoading(true);
				} else if (status === EJournalStatus.DRAFT) {
					setDraftIsLoading(true);
				}
				await postApi('/journal/create', {
					companyId: localStorage.getItem('companyId'),
					payPeriodId: payPeriodId,
					date: journalData?.journalDate,
					qboJournalNo: String(journalData?.journalNumber),
					status: status,
					notes: journalData?.notes,
					dateString: moment
						.utc(journalData?.journalDate?.$d)
						.format('YYYY-MM-DD'),
				});

				toastText(
					`Journal ${journalExists ? 'edited' : 'created'} successfully`,
					'success'
				);
				setPublishIsLoading(false);
				setDraftIsLoading(false);
				if (getJournalListingData) {
					getJournalListingData();
				}
				handleClosePreviewJournal();
			}
		} catch (err: any) {
			setPublishIsLoading(false);
			setDraftIsLoading(false);

			// Check for duplicate journal error with attemptDetails
			if (
				err?.response?.data?.error?.attemptDetails &&
				Array.isArray(err?.response?.data?.error?.attemptDetails) &&
				err?.response?.data?.error?.attemptDetails.length > 0
			) {
				setDuplicateErrorData({
					attempts: err?.response?.data?.error?.attempts || err?.response?.data?.error?.attemptDetails.length,
					attemptDetails: err?.response?.data?.error?.attemptDetails,
				});
				setShowDuplicateErrorModal(true);
			} else if (err?.response?.status === 504) {
				toastText(
					'The request timed out. This may be due to a large journal entry exceeding supported limits.',
					'error'
				);
			} else if (
				err?.response?.data?.message?.includes('outdated class reference')
			) {
				toastText(err.response.data.message, 'error');
				setShowResolveClassModal(true);
			} else if (!err?.response) {
				toastText('Something went wrong.', 'error');
			} else {
				toastText(
					err?.response?.data?.message ||
					'An unexpected error occurred while submitting the journal.',
					'error'
				);
			}
			// toastText(err?.response?.data?.message, 'error');
			// handleClosePreviewJournal();
		}
	};

	// const createCostAllocationPdf = async () => {
	// 	const query = {
	// 		companyId: localStorage.getItem('companyId'),
	// 		payPeriodId: payPeriodId,
	// 	};

	// 	const response = await getApi('/cost-allocation/export-pdf', query);
	// 	const blob = base64ToBlob(response.data.data, 'application/pdf');
	// 	const blobUrl = URL.createObjectURL(blob);
	// 	setAttachmentData(blobUrl);
	// };
	const createCostAllocationCsv = async () => {
		const query = {
			companyId: localStorage.getItem('companyId'),
			payPeriodId: payPeriodId,
		};

		const response = await getApi('/cost-allocation/export-csv2', query);
		const blob = base64ToBlob(response.data.data, 'text/csv');
		const blobUrl = URL.createObjectURL(blob);
		const fileName = moment
			.tz(new Date(), TIMEZONE)
			.format('MMDDYYYYhhmmss');
		setAttachmentData(blobUrl);
		setAttachmentFileName(`CostAllocation_${fileName}.csv`);
	};

	const fetchJournalData = async () => {
		try {
			await dispatch(
				previewJournalAction({
					payPeriodId,
				})
			);
		} catch (err) {
			toastText('Something went wrong in preview journal', 'error');
		}
	};

	const fetchJournalDetails = async () => {
		try {
			const response: any = await getApi('/journal/by-payPeriod', {
				companyId: localStorage.getItem('companyId'),
				payPeriodId,
			});
			// if (response.data.data) {
			// 	setJournalData((prevData: any) => {
			// 		return {
			// 			...prevData,
			// 			journalDate: dayjs(response?.data?.data?.date),
			// 			journalNumber: response?.data?.data?.qboJournalNo,
			// 			// notes: response?.data?.data?.notes,
			// 			status: response?.data?.data?.status,
			// 			qboJournalNumber: response?.data?.data?.qboJournalNo,
			// 		};
			// 	});
			// 	setJournalExists(true);
			// }

			if (response.data.data) {
				setJournalData((prevData: any) => {
					return {
						...prevData,
						journalDate: response?.data?.data?.date
							? dayjs(response?.data?.data?.date).tz(TIMEZONE)
							: dayjs(payPeriodLastDate).tz(TIMEZONE, true),
						journalNumber: response?.data?.data?.qboJournalNo,
						notes: response?.data?.data?.notes,
						status: response?.data?.data?.status,
						qboJournalNumber: response?.data?.data?.qboJournalNo,
					};
				});
				setJournalExists(true);
			} else {
				const latestJournalNo: any = await getApi('/journal/latest-no', {
					companyId: localStorage.getItem('companyId'),
				});
				if (latestJournalNo.data.data) {
					setJournalData({
						...journalData,
						journalNumber: latestJournalNo.data.data.latestNo,
					});
					setAutoJournalNo(latestJournalNo.data.data.latestNo);
				}
				setJournalExists(false);
			}
		} catch (err: any) {
			if (err?.response?.data?.error?.status == 401) {
				navigate('/login');
				window.location.reload();
			} else {
				toastText('Something went wrong in preview journal', 'error');
			}
		}
	};

	return (
		<>
			<Modal
				open={isPreviewJournalOpen}
				onCancel={handleClosePreviewJournal}
				className="preview-container"
				closable={false}
				style={{
					borderRadius: 0,
				}}
				footer={[]}
			>
				<div>
					<Row className={styles['preview-container__top']}>
						<div className={styles['preview-container__top-title']}>
							<b>Journal Preview</b>
						</div>
						<div
							className={styles['preview-container__top-close']}
							onClick={handleClosePreviewJournal}
						>
							<CloseSvg />
						</div>
					</Row>
					<div
						className={styles['preview-container__content']}
						ref={modalContentRef}
						style={{
							maxHeight: 'calc(100vh - 100px)',
							overflowY: 'auto',
							position: 'relative',
						}}
					>
						{' '}
						<div className={styles['preview-container__content--header']}>
							<p className={styles['preview-container__content--header-title']}>
								Journal Entry{' '}
								{journalData?.qboJournalNumber && journalData?.status === 1
									? `#${journalData?.qboJournalNumber}`
									: ``}
							</p>
							<div className={styles['preview-container__content--header-date']}>
								Journal Date:{' '}
								<DatePicker
									onChange={dateChangeHandler}
									value={
										journalData?.journalDate
											? dayjs(journalData?.journalDate)
											: dayjs(payPeriodLastDate).tz(TIMEZONE, true)
									}
									format={'MM-DD-YYYY'}
									allowClear={false}
									size="large"
									disabled={
										(journalExists && !isJournalEntriesPermissionEdit) ||
										(!journalExists && !isJournalEntriesPermissionAdd) ||
										isAccountClosed
									}
								/>
							</div>
							{/* workig but hide in ui */}
							{/* <div
							className={styles['preview-container__content--header-number']}
						>
							Journal No:
							<InputNumber
								value={journalData?.journalNumber}
								onChange={journalNoChangeHandler}
								onBlur={(e: any) => {
									const regex = /^(0*[1-9][0-9]*(\.\d+)?)$/;

									if (!regex.test(e.target.value)) {
										setJournalData((prevData: any) => {
											return {
												...prevData,
												journalNumber: autoJournalNo,
											};
										});
										return;
									}
								}}
								disabled={
									(journalExists && !isJournalEntriesPermissionEdit) ||
									(!journalExists && !isJournalEntriesPermissionAdd) ||
									isAccountClosed
								}
							/>
						</div> */}
						</div>
						<div className={styles['preview-container__content--table']}>
							<Table
								dataSource={journalEntries}
								sticky
								pagination={false}
								style={{ borderRadius: 0 }}
								loading={{
									spinning: isLoading,
									indicator: (
										<div
											style={{
												textAlign: 'center',
												padding: '50px 0',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												minWidth: '200px',
												marginLeft: '-100px',
												marginTop: '12px',
											}}
										>
											<Spin
												indicator={
													<LoadingOutlined
														spin
														style={{ color: '#485949', fontSize: '30px' }}
													/>
												}
												className="custom-spinner"
											/>
											<div
												style={{
													fontSize: '16px',
													color: '#485949',
													fontWeight: '600',
													minHeight: '20px',
													transition: 'opacity 0.3s ease-in-out',
													whiteSpace: 'nowrap',
													textAlign: 'center',
													paddingTop: '40px',
													marginLeft: '14px',
												}}
											>
												{currentLoadingMessage}
											</div>
										</div>
									),
								}}
								rowKey={(record: any) => {
									return record.key;
								}}
								rowClassName={(record: any) => {
									if (record.account === 'Total') {
										return 'journal-total-row';
									}
									return '';
								}}
							>
								<Column
									// title="#"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											#
										</p>
									)}
									dataIndex="id"
									key="id"
									width={'6%'}
									className="bg-white journal-dashed-border"
									render={(value: string | number) => {
										return (
											<span className={styles['dynamic-table__hash']}>
												{value}
											</span>
										);
									}}
								/>
								<Column
									// title="Account"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Account
										</p>
									)}
									dataIndex="account"
									key="account"
									width={'17%'}
									className="bg-white journal-dashed-border"
									render={(value: string | number, record: any) => {
										return (
											<div className={styles['dynamic-table__account']}>
												{value} <br />
												{record?.ruleName ? (
													<Tag color="#108ee9">{record?.ruleName}</Tag>
												) : null}
											</div>
										);
									}}
								/>
								<Column
									// title="Debit(+)"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Debit(+)
										</p>
									)}
									dataIndex="debit"
									key="debit"
									width = {'13%'}
									className="bg-white journal-dashed-border"
									align="right"
									render={(value: string | number, record: any) => {
										return (
											<NumericFormat
												prefix={record.account === 'Total' ? '$' : ''}
												value={value}
												thousandSeparator=","
												decimalScale={
													configurationsData?.decimalToFixedAmount || 2
												}
												fixedDecimalScale={true}
												displayType="text"
												renderText={(value) => (
													<p
														className={styles['dynamic-table-total']}
														style={{
															textAlign: 'right',
															fontSize: '12px',
														}}
													>
														{value ?? 0}
													</p>
												)}
											/>
										);
									}}
								/>
								<Column
									// title="Credit(-)"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Credit(-)
										</p>
									)}
									dataIndex="credit"
									key="credit"
									width = {'13%'}
									className="bg-white journal-dashed-border credit-font"
									align="right"
									render={(value: string | number, record: any) => {
										return (
											<NumericFormat
												prefix={record.account === 'Total' ? '$' : ''}
												value={value}
												thousandSeparator=","
												decimalScale={
													configurationsData?.decimalToFixedAmount || 2
												}
												fixedDecimalScale={true}
												displayType="text"
												renderText={(value) => (
													<p
														className={styles['dynamic-table-total']}
														style={{
															textAlign: 'right',
															fontSize: '12px',
														}}
													>
														{value ?? 0}
													</p>
												)}
											/>
										);
									}}
								/>
								{!isExcludeEmployeeEnabled && (
									<Column
										// title="Employee Name"
										title={() => (
											<p
												style={{
													fontSize: '12px',
												}}
											>
												Employee Name
											</p>
										)}
										dataIndex="employeeName"
										key="employeeName"
										width = {'17%'}
										className="bg-white journal-dashed-border"
										render={(value: string | number) => {
											return (
												<span className={styles['dynamic-table__employee']}>
													{value}
												</span>
											);
										}}
									/>
								)}
								<Column
									// title="Customer Name"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Customer Name
										</p>
									)}
									dataIndex="customer"
									key="customer"
									width = {'17%'}
									className="bg-white journal-dashed-border"
									render={(value: string | number) => {
										return (
											<span className={styles['dynamic-table__customer']}>
												{value}
											</span>
										);
									}}
								/>
								<Column
									// title="Class"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Class
										</p>
									)}
									dataIndex="class"
									key="class"
									width = {'17%'}
									className="bg-white journal-dashed-border"
									render={(value: string | number) => {
										return (
											<span className={styles['dynamic-table__class']}>
												{value}
											</span>
										);
									}}
								/>
								{/* <Column
								title={() => (
									<p
										style={{
											fontSize: '12px',
										}}
									>
										Location
									</p>
								)}
								dataIndex="location"
								key="location"
								className="bg-white"
								render={(value: string | number) => {
									return (
										<span className={styles['dynamic-table__location']}>
											{value}
										</span>
									);
								}}
							/> */}
							</Table>
						</div>
						<div className={styles['preview-container__content--footer']}>
							<Row gutter={32}>
								<Col xs={16} sm={16} md={16} lg={16}>
									<div
										className={styles['preview-container__content--footer-notes']}
									>
										<label style={{ fontSize: '12px' }} htmlFor="my-textarea">
											Notes:
										</label>
										<TextArea
											rows={3}
											className={
												styles['preview-container__content--footer-textarea']
											}
											value={journalData?.notes}
											onChange={notesHandler}
											disabled={
												(journalExists && !isJournalEntriesPermissionEdit) ||
												(!journalExists && !isJournalEntriesPermissionAdd) ||
												isAccountClosed
											}
										/>
									</div>
								</Col>

								<Col
									xs={8}
									sm={8}
									md={8}
									lg={8}
									className={styles['preview-container__content--footer-upload']}
								>
									<p style={{ fontSize: '12px' }}>Attachment: </p>
									<a
										href={attachmentData}
										download={attachmentFileName || undefined}
										target={attachmentFileName ? undefined : '_blank'}
										rel="noreferrer"
										style={{ fontSize: '12px' }}
									>
										Cost Allocation Report of the Pay Period : {startDate} -{' '}
										{endDate}
									</a>
								</Col>
							</Row>
							<div
								className={styles['preview-container__content--footer-buttons']}
								key={Math.random()}
							>
								{!(
									(journalExists && !isJournalEntriesPermissionEdit) ||
									(!journalExists && !isJournalEntriesPermissionAdd) ||
									isAccountClosed
								) ? (
									<>
										<button
											className={`${styles[
												'preview-container__content--footer-buttons-button'
											]
												} ${(isLoading || publishIsLoading || draftIsLoading) &&
												'pointer-event-none'
												}`}
											onClick={() => handleSubmit(EJournalStatus.PUBLISHED)}
											disabled={
												isLoading ||
												(journalExists && !isJournalEntriesPermissionEdit) ||
												(!journalExists && !isJournalEntriesPermissionAdd)
											}
										>
											{publishIsLoading ? (
												<img src="/assets/gifs/loading-black.gif" height={40} />
											) : (
												'Save & Publish'
											)}
										</button>
										{!journalExists ||
											(journalExists &&
												journalData.status != EJournalStatus.PUBLISHED) ? (
											<button
												className={`${styles[
													'preview-container__content--footer-buttons-button'
												]
													} ${(isLoading ||
														publishIsLoading ||
														draftIsLoading ||
														(journalExists && !isJournalEntriesPermissionEdit) ||
														(!journalExists && !isJournalEntriesPermissionAdd)) &&
													'pointer-event-none'
													}`}
												onClick={() => handleSubmit(EJournalStatus.DRAFT)}
												disabled={
													isLoading ||
													(journalExists && !isJournalEntriesPermissionEdit) ||
													(!journalExists && !isJournalEntriesPermissionAdd)
												}
											>
												{draftIsLoading ? (
													<img src="/assets/gifs/loading-black.gif" height={40} />
												) : (
													'Save as Draft'
												)}
											</button>
										) : null}
									</>
								) : null}
								<button
									className={
										styles['preview-container__content--footer-buttons-cancel']
									}
									onClick={handleClosePreviewJournal}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>

					{/* Scroll to bottom button */}
					{/* Scroll to bottom button with Ant Design */}
					{showScrollToBottom && (
						<div
							style={{
								position: 'fixed',
								right: '60px',
								bottom: '100px',
								zIndex: 1000,
							}}
						>
							<button
								onClick={scrollToBottom}
								style={{
									width: '48px',
									height: '48px',
									borderRadius: '50%',
									border: '1px solid #344734',
									padding: 0,
									margin: 0,
									backgroundColor: '#fff',
									color: '#344734',
									cursor: 'pointer',
									boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '32px',
									fontWeight: 'bold',
									transition: 'all 0.3s ease',
									outline: 'none',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.backgroundColor = '#344734';
									e.currentTarget.style.color = '#fff';
									e.currentTarget.style.transform = 'scale(1.1)';
									e.currentTarget.style.boxShadow =
										'0 6px 16px rgba(52, 71, 52, 0.3)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.backgroundColor = '#fff';
									e.currentTarget.style.color = '#344734';
									e.currentTarget.style.transform = 'scale(1)';
									e.currentTarget.style.boxShadow =
										'0 4px 12px rgba(0, 0, 0, 0.15)';
								}}
								title="Scroll to bottom"
							>
								<DownCircleOutlined />
							</button>
						</div>
					)}
				</div>
			</Modal>
			<DuplicateJournalErrorModal
				isOpen={showDuplicateErrorModal}
				errorData={duplicateErrorData}
				onClose={() => {
					setShowDuplicateErrorModal(false);
					setDuplicateErrorData(null);
				}}
			/>
			{showResolveClassModal && (
				<ResolveClassReferencesModal
					open={showResolveClassModal}
					onClose={() => setShowResolveClassModal(false)}
					companyId={localStorage.getItem('companyId') as string}
					payPeriodId={payPeriodId as string}
				/>
			)}
		</>
	);
};

export default PreviewJournal;
